import type {
  TBlockConfig,
  TBlockWithoutEntryBlock,
} from '@conversed/shared/src/central/types/block.type';
import type { Decider } from '@conversed/shared/src/central/types/blocks/Decider';
import type { IntentCheckBlock } from '@conversed/shared/src/central/types/blocks/IntentCheckBlock';
import type { LLMBlock } from '@conversed/shared/src/central/types/blocks/LLMBlock';
import type { TextWithButtons } from '@conversed/shared/src/central/types/blocks/TextWithButtons';
import type { TextV2Block } from '@conversed/shared/src/central/types/blocks/v2/TextV2';
import {
  pushUnique,
  removeValueFromList,
} from '@conversed/shared/src/helpers/data.helpers';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Actions } from '../../../constants/actionTypes';
import {
  getConnectionLimitByRouterParams,
  getRulesByRouterParams,
} from '../../../helpers/flowHelper';
import { mainActions } from '../../../redux/reducers/mainHandler';
import { useModal } from '../../modal/useModal';
import { useMainDispatch } from '../../useMainDispatch';
import { useBlocks } from '../block/useBlocks';
import { useBlocksUpdates } from '../block/useBlocksUpdates';

export const useFlowActions = () => {
  const { gates, blocks } = useBlocks();
  const { updateBlocks, deleteBlocks } = useBlocksUpdates();
  const newDispatch = useDispatch();
  const dispatch = useMainDispatch();
  const { showModal } = useModal();

  const setSelectedBlock = useCallback(
    (selectedBlock: TBlockConfig) => {
      dispatch({
        type: Actions.SET_DESIGNER,
        payload: { selectedBlock },
      });
    },
    [dispatch],
  );

  const clearSelectedBlock = useCallback(() => {
    dispatch({
      type: Actions.SET_DESIGNER,
      payload: { selectedBlock: undefined },
    });
  }, [dispatch]);

  const setTargetBlock = useCallback(
    (targetBlock: TBlockConfig) => {
      dispatch({ type: Actions.SET_DESIGNER, payload: { targetBlock } });
    },
    [dispatch],
  );

  const setMatchIndex = (index: number) => {
    dispatch({
      type: Actions.SET_DESIGNER,
      payload: { blocksSearchMatchIndex: index },
    });
  };

  const setSearchString = (input: string) => {
    dispatch({
      type: Actions.SET_DESIGNER,
      payload: { blocksSearchString: input },
    });
  };

  const connectBlockToRouter = useCallback(
    (routerBlock: Decider, targetBlock: TBlockConfig) => {
      const { id: targetBlockId } = targetBlock;
      const { params: routerParams } = routerBlock;

      const connectionLimit = getConnectionLimitByRouterParams(routerParams);
      const currentConnectionsCount = routerParams.possibleDestinations.length;

      const canConnect = connectionLimit > currentConnectionsCount;

      if (!canConnect) {
        return routerBlock;
      }

      return {
        ...routerBlock,
        params: {
          ...routerBlock.params,
          possibleDestinations: [
            ...Object.values(routerBlock.params.possibleDestinations),
            {
              blockId: targetBlockId,
              rules: getRulesByRouterParams(routerParams),
            },
          ],
        },
      };
    },
    [],
  );

  type THandles = {
    source?: string | null;
    target?: string | null;
  };

  const connectBlocks = useCallback(
    (
      handles: THandles,
      sourceId?: string | null,
      targetId?: string | null,
    ): TBlockConfig[] | undefined => {
      const sourceBlock = blocks?.find(({ id }) => id === sourceId);
      const targetBlock = blocks?.find(({ id }) => id === targetId);

      if (!sourceBlock || !targetBlock) {
        return;
      }

      const updateOperations: TBlockConfig[] = [];

      // * Special case, target of entry node always becomes the entry block
      if (sourceBlock.type === 'entry') {
        blocks
          // * Filter out blocks that cannot have isEntryBlock set
          ?.filter(
            (block): block is TBlockWithoutEntryBlock =>
              block.type !== 'version' &&
              block.type !== 'button' &&
              block.type !== 'entry',
          )
          ?.forEach((block) => {
            // * We have to remove the isEntryBlock from any block that has it set
            // * Also set isEntryBlock to true for target block
            if (
              ('isEntryBlock' in block && block.isEntryBlock) ||
              targetId === block.id
            ) {
              updateOperations.push({
                ...block,
                isEntryBlock: targetId === block.id,
              });

              return updateBlocks(updateOperations);
            }
          });
      }

      if (sourceBlock.type === 'imageSlider') {
        const { source } = handles;

        // * source type id should be: handle-for-${sliderPosition}-button-${index}
        if (source) {
          const [imageIndex, buttonIndex] =
            source.match(/\d/g)?.map((item) => Number.parseInt(item)) ?? [];

          const image = sourceBlock.images[imageIndex];
          const button = {
            ...sourceBlock.images[imageIndex].buttons[buttonIndex],
            destination: targetId ?? '',
          };

          const newImage = {
            ...image,
            buttons: [
              ...image.buttons.slice(0, buttonIndex),
              button,
              ...image.buttons.slice(buttonIndex + 1),
            ],
          };

          updateOperations.push({
            ...sourceBlock,
            images: [
              ...sourceBlock.images.slice(0, imageIndex),
              newImage,
              ...sourceBlock.images.slice(imageIndex + 1),
            ],
          });

          updateBlocks(updateOperations);
          return;
        }
      }

      // * If block is handover set target block as returnDestination
      if (sourceBlock.type === 'handover' && targetId) {
        updateOperations.push({
          ...sourceBlock,
          returnDestination: [targetId],
        });
      }

      blocks?.forEach((block) => {
        // * Leave block unchanged if it is not target or source
        if (![sourceBlock.id, targetBlock.id].includes(block.id)) {
          return;
        }

        // * Dragging from a dest-{n} handle rewires that destination slot to the new block
        if (
          sourceBlock.type === 'Decider' &&
          handles.source?.startsWith('dest-')
        ) {
          const destIndex = Number(handles.source.split('-')[1]);
          const updatedDestinations = [
            ...sourceBlock.params.possibleDestinations,
          ];
          updatedDestinations[destIndex] = {
            ...updatedDestinations[destIndex],
            blockId: targetBlock.id,
          };
          return updateOperations.push({
            ...sourceBlock,
            params: {
              ...sourceBlock.params,
              possibleDestinations: updatedDestinations,
            },
          });
        }

        if (sourceBlock.type === 'Decider') {
          try {
            if (sourceBlock.params.logic === 'decideBasedOnPromotion') {
              setSelectedBlock(sourceBlock);
              setTargetBlock(targetBlock);
              return showModal('edit-router-ref-param');
            }

            if (
              [
                'decideBasedOnRules',
                'decideByNumber',
                'decideBasedOnText',
                'decideByLLMQuestionCount',
              ].includes(sourceBlock.params.logic)
            ) {
              setSelectedBlock(sourceBlock);
              setTargetBlock(targetBlock);
              return showModal('edit-rules-param');
            }

            const connectedRouter = connectBlockToRouter(
              sourceBlock,
              targetBlock,
            );
            return updateOperations.push(connectedRouter);
          } catch (error) {
            // TODO: Handle error
            console.error(error);
            return;
          }
        }

        if (
          sourceBlock.id === block.id &&
          (targetBlock.type === 'button' || targetBlock.type === 'shortcut')
        ) {
          if (sourceBlock.type === 'text-v2') {
            return updateOperations.push({
              ...block,
              type: 'text-v2' as const,
              buttons: pushUnique(targetBlock.id, sourceBlock?.buttons),
            } as TextV2Block);
          }

          // * Button and shortcut support for llm block
          if (sourceBlock.type === 'llm') {
            return updateOperations.push({
              ...block,
              type: 'llm' as const,
              buttons: pushUnique(targetBlock.id, sourceBlock?.buttons),
            } as LLMBlock);
          }

          if (
            sourceBlock.type === 'text' ||
            sourceBlock.type === 'textWithButtons'
          ) {
            return updateOperations.push({
              ...block,
              type: 'textWithButtons' as const,
              contains: pushUnique(
                targetBlock.id,
                sourceBlock.type === 'textWithButtons'
                  ? sourceBlock?.contains
                  : [],
              ),
            } as TextWithButtons);
          }

          if (sourceBlock.type === 'image') {
            return updateOperations.push({
              ...sourceBlock,
              contains: pushUnique(targetBlock.id, sourceBlock?.contains ?? []),
            });
          }
        }

        // * OTP and other cases, set target as error destination
        if (
          sourceBlock.id === block.id &&
          (handles.source === 'mid' || handles.source === 'error') &&
          'errorDestination' in block
        ) {
          return updateOperations.push({
            ...block,
            errorDestination: [targetBlock.id],
          });
        }

        // * Intent check — route to low / medium / high destination by handle
        if (
          sourceBlock.id === block.id &&
          sourceBlock.type === 'intent-check'
        ) {
          const intentBlock = block as IntentCheckBlock;
          if (handles.source === 'low') {
            return updateOperations.push({
              ...intentBlock,
              lowDestination: [targetBlock.id],
            });
          }
          if (handles.source === 'medium') {
            return updateOperations.push({
              ...intentBlock,
              mediumDestination: [targetBlock.id],
            });
          }
          // 'end' handle → high destination
          return updateOperations.push({
            ...intentBlock,
            destination: [targetBlock.id],
          });
        }

        // * Catch-all, default case, set target as destination
        if (sourceBlock.id === block.id && 'destination' in block) {
          return updateOperations.push({
            ...block,
            destination: [targetBlock.id],
          });
        }
      });

      updateBlocks(updateOperations);
    },
    [
      blocks,
      connectBlockToRouter,
      setSelectedBlock,
      setTargetBlock,
      showModal,
      updateBlocks,
    ],
  );

  // * After block deletion we need to delete said block reference from all other connected blocks
  const disconnectBlock = useCallback(
    (targetId?: string | null) => {
      if (!targetId) {
        return;
      }

      const updateOperations: TBlockConfig[] = [];

      blocks?.forEach((block) => {
        // * Intent check — clean up all 3 destination fields in one pass
        if (block.type === 'intent-check') {
          const intentBlock = block as IntentCheckBlock;
          const updatedBlock: IntentCheckBlock = { ...intentBlock };
          let changed = false;

          if (intentBlock.lowDestination?.includes(targetId)) {
            updatedBlock.lowDestination = removeValueFromList(
              (blockId) => blockId === targetId,
              intentBlock.lowDestination,
            );
            changed = true;
          }
          if (intentBlock.mediumDestination?.includes(targetId)) {
            updatedBlock.mediumDestination = removeValueFromList(
              (blockId) => blockId === targetId,
              intentBlock.mediumDestination,
            );
            changed = true;
          }
          if (intentBlock.destination?.includes(targetId)) {
            updatedBlock.destination = removeValueFromList(
              (blockId) => blockId === targetId,
              intentBlock.destination,
            );
            changed = true;
          }
          if (intentBlock.errorDestination?.includes(targetId)) {
            updatedBlock.errorDestination = removeValueFromList(
              (blockId) => blockId === targetId,
              intentBlock.errorDestination,
            );
            changed = true;
          }
          if (changed) {
            return updateOperations.push(updatedBlock);
          }
          return;
        }

        if ('destination' in block && block.destination?.includes(targetId)) {
          return updateOperations.push({
            ...block,
            destination: removeValueFromList(
              (blockId) => blockId === targetId,
              block.destination,
            ),
          });
        }

        if ('contains' in block && block.contains?.includes(targetId)) {
          return updateOperations.push({
            ...block,
            contains: removeValueFromList(
              (blockId) => blockId === targetId,
              block.contains,
            ),
          });
        }

        if ('buttons' in block && block.buttons?.includes(targetId)) {
          return updateOperations.push({
            ...block,
            buttons: removeValueFromList(
              (blockId) => blockId === targetId,
              block.buttons,
            ),
          });
        }
      });

      updateBlocks(updateOperations);
      deleteBlocks([targetId]);
      clearSelectedBlock();
    },
    [blocks, clearSelectedBlock, updateBlocks, deleteBlocks],
  );

  const setSelectedSubFlow = useCallback(
    (subFlow: string) => {
      newDispatch(mainActions.setSubFlowToDisplay(subFlow));
    },
    [newDispatch],
  );

  const addSubFlow = useCallback(
    (subFlow: string) => {
      newDispatch(mainActions.addSubFlowForFlow(subFlow));
    },
    [newDispatch],
  );

  const updateSubFlow = useCallback(
    (
      oldSubFlow: string,
      newSubFlow: string,
      oldRevisionId?: string,
      newRevisionId?: string,
    ) => {
      if (oldSubFlow !== newSubFlow) {
        newDispatch(
          mainActions.updateSubFlow({
            oldName: oldSubFlow,
            newName: newSubFlow,
            // * If the revision is not changed, then we need to navigate to the new subflow because we are in the same revision
            // * If the revision is changed, then we do not need to navigate to the new subflow because we are in a different revision,
            // so we need to navigate to the 'main' subflow
            navigateTo: oldRevisionId === newRevisionId ? newSubFlow : 'main',
          }),
        );
      }

      if (oldRevisionId !== newRevisionId) {
        newDispatch(
          mainActions.removeSubFlowFromFlow(
            oldSubFlow !== newSubFlow ? oldSubFlow : newSubFlow,
          ),
        );
      }

      const updatedBlocks = blocks
        .filter((block) => block.subFlow === oldSubFlow)
        .map((block) => ({
          ...block,
          subFlow: newSubFlow,
          revisionId: newRevisionId,
        }));

      // * Update target gates in addition to blocks when updating subflows
      const updatedGates = gates
        .filter((gate) => gate.routeToSubFlow === oldSubFlow)
        .map((gate) => ({
          ...gate,
          routeToSubFlow: newSubFlow,
        }));

      updateBlocks([...updatedBlocks, ...updatedGates]);
    },
    [newDispatch, blocks, gates, updateBlocks],
  );

  const removeSubFlow = useCallback(
    (subFlow: string) => {
      const blockIds = blocks
        .filter((block) => block.subFlow === subFlow)
        .map((block) => block.id);
      deleteBlocks(blockIds);
      newDispatch(mainActions.removeSubFlowFromFlow(subFlow));
    },
    [blocks, deleteBlocks, newDispatch],
  );

  return {
    setSelectedBlock,
    clearSelectedBlock,
    setTargetBlock,
    setMatchIndex,
    setSearchString,
    connectBlocksByIds: connectBlocks,
    deleteBlockFromFlow: disconnectBlock,
    updateBlocks,
    deleteBlocks,
    updateBlock: (block: TBlockConfig) => updateBlocks([block]),
    setSelectedSubFlow,
    addSubFlow,
    removeSubFlow,
    updateSubFlow,
  };
};
